import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { CreditCard, ExternalLink, AlertCircle } from 'lucide-react';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { usePostApiWorkspacesWorkspaceIdBillingCreatePortalSession } from '@/api/generated/billing/billing';
import { toast } from 'sonner';

export const ActiveSubscriptionWarningModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspaceStore();

  const { mutate: createPortalSession, isPending } =
    usePostApiWorkspacesWorkspaceIdBillingCreatePortalSession({
      mutation: {
        onSuccess: (data: any) => {
          if (data?.portalUrl || data?.url) {
            window.location.href = data.portalUrl || data.url;
          } else {
            navigate('/billing');
            setOpen(false);
          }
        },
        onError: () => {
          toast.error('Não foi possível abrir o portal de faturamento. Redirecionando para a página de faturamento.');
          navigate('/billing');
          setOpen(false);
        },
      },
    });

  useEffect(() => {
    const handleEvent = () => setOpen(true);
    window.addEventListener('active_subscription_prevents_deletion', handleEvent);
    return () => window.removeEventListener('active_subscription_prevents_deletion', handleEvent);
  }, []);

  const handleOpenBillingPortal = () => {
    if (activeWorkspaceId) {
      createPortalSession({ workspaceId: activeWorkspaceId });
    } else {
      navigate('/billing');
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-500">
            <AlertCircle className="h-5 w-5" />
            <DialogTitle>Assinatura Ativa Detectada</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-left space-y-2">
            <span>
              Este workspace possui uma assinatura ativa do Stripe. Para evitar cobranças indevidas, cancele sua assinatura no portal de faturamento antes de excluir o workspace.
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Fechar
          </Button>
          <Button onClick={handleOpenBillingPortal} disabled={isPending} className="gap-2">
            <CreditCard className="h-4 w-4" />
            <span>{isPending ? 'Abrindo portal...' : 'Gerenciar / Cancelar no Stripe'}</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
